import React from 'react';
import classnames from 'classnames/bind';
import { DateTime } from 'luxon';
import {
  Card,
  Image,
  Title,
  Text,
  Button as MantineButton,
  Group,
  Badge,
  Skeleton,
} from '@mantine/core';
import { Link } from 'react-router-dom';

import AIModel from 'types/ai_model';

import { parseModelLabels } from 'services/model_labels';
import { useAPI } from 'hooks/api/useAPI';
import { useIsLumeoAdmin } from 'hooks/useAuth';
import { useReadFilesFromURL } from 'hooks/useReadFilesFromURL';

import { Icon } from 'components/Icon/IconDeprecated';
import { Button } from 'components/Button/Button';
import { Popover } from 'components/Popover/Popover';
import { PricingPlanLabel } from 'pipelines/components/PricingPlanLabel/PricingPlanLabel';

import styles from './ModelCard.module.scss';

const c = classnames.bind(styles);

type ModelCardProps = {
  model: AIModel;
};

export function ModelCard({ model }: ModelCardProps) {
  const { applicationID } = useAPI();

  const isAdmin = useIsLumeoAdmin();
  const canEdit = !model.isMarketplaceModel() || isAdmin;
  const isIncludedInPlan = model.is_included_in_plan !== false;

  const [isLoading, setIsLoading] = React.useState(true);
  const [isLabelsOpen, setIsLabelsOpen] = React.useState(false);
  const shouldFetchLabels =
    isLabelsOpen && !model.labels && Boolean(model.labels_file_url);
  const [{ data: labelsFile, isInitialLoading: isLoadingLabels }] =
    useReadFilesFromURL(shouldFetchLabels ? model.labels_file_url : undefined);
  const labels = parseModelLabels(model.labels ?? labelsFile);

  const detailURL = model.isMarketplaceModel()
    ? `/applications/${applicationID}/admin/marketplace-models/${model.id}`
    : `/applications/${applicationID}/design/models/${model.id}`;

  function renderLabelsContent() {
    if (isLoadingLabels) {
      return <Text c="dimmed">Loading labels...</Text>;
    }

    if (!labels.length) {
      return <Text c="dimmed">No labels available.</Text>;
    }

    return (
      <div className={c('labels-list')}>
        {labels.map((label) => (
          <Badge key={label} variant="light">
            {label}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <Card
      className={c('model-card', {
        'upgrade-required': !isIncludedInPlan,
      })}
      withBorder
    >
      {model.gallery_img_url && (
        <Card.Section>
          <Skeleton radius="0" visible={isLoading}>
            <Image
              className={c('image')}
              src={model.gallery_img_url}
              onLoad={() => setIsLoading(false)}
              alt=""
              fit="cover"
            />
          </Skeleton>
        </Card.Section>
      )}

      {!isIncludedInPlan && <PricingPlanLabel placement="top-right" hasLink />}

      <Group
        className={c('metadata', {
          'with-upgrade-plan-label': !isIncludedInPlan,
        })}
        gap="xs"
        mx="-2px"
        mb="sm"
        mt={model.gallery_img_url && 'md'}
      >
        {!model.isMarketplaceModel() && (
          <Badge size="md">{model.architecture}</Badge>
        )}
        <Badge size="md">{model.capability}</Badge>
      </Group>

      <Title order={3}>{model.name}</Title>

      {model.updated_at && (
        <Text mt="xs">{DateTime.fromISO(model.updated_at).toRelative()}</Text>
      )}

      <Text
        mt="xs"
        mb="md"
        lineClamp={model.isMarketplaceModel() ? undefined : 3}
      >
        {model.description}
      </Text>

      <div className={c('actions')}>
        <Popover
          open={isLabelsOpen}
          onOpenChange={setIsLabelsOpen}
          side="bottom"
          sideOffset={4}
          trigger={
            <Button
              className={c('labels-button')}
              size="xsmall"
              variant="secondary"
            >
              <Icon name="tag" size="small" />
              <span className={c('labels-button-text')}>Labels</span>
            </Button>
          }
        >
          <div className={c('labels-popover')}>
            <Text fw={600} mb="xs">
              Labels
            </Text>
            {renderLabelsContent()}
          </div>
        </Popover>

        {canEdit && (
          <MantineButton
            className={c('button')}
            leftSection={<Icon name="edit" size="small" />}
            component={Link}
            size="sm"
            to={detailURL}
          >
            Edit
          </MantineButton>
        )}
      </div>
    </Card>
  );
}
